import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditStandard } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStandard } from "@shared/models/card-stack"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { partial } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  cardId: string
  standards: Array<CardStandard>
  previousStandards: Array<CardStandard>
}

export default function lessonStandardsReplaceAction(payload: Payload): PreparedAction {
  return {
    name: "LESSON_STANDARDS_REPLACE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.cardId,
  }).set("embedded", "attributes.standards", payload.standards).patch

  return [patch]
}

function undoPatches(payload: Payload) {
  let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
    id: payload.cardId,
  }).set(
    "embedded",
    "attributes.standards",
    JSON.parse(JSON.stringify(payload.previousStandards))
  ).patch

  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultLessonModels(payload, findFn, userId)
  let diff = payload.standards?.length - payload.previousStandards.length
  let verb = diff > 0 ? "Added" : "Removed"
  let noun = diff === 1 ? "standard" : "standards"
  let absoluteDiff = Math.abs(diff)
  let titleForAnalytics
  let adverbialObjectSpecifier: "lesson" | "unit"
  if (models.cardStackType === "unit") {
    titleForAnalytics = "Lesson - Update Standards - Unit"
    adverbialObjectSpecifier = "unit"
  } else {
    titleForAnalytics = "Lesson - Update Standards"
    adverbialObjectSpecifier = "lesson"
  }
  let newEvent: LessonEditStandard = {
    ...createBlankEvent(),
    root_id: models.planbook?.id ?? null,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStackId,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
    primary_object_type: "lesson",
    primary_object_specifier: "standards",

    adverbial_verb: "adding",
    adverbial_object_type: "standard",
    adverbial_object_specifier: adverbialObjectSpecifier,
    adverbial_object_id: payload.cardId,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
  }
  return {
    title: "Changed Lesson Standards",
    titleForAnalytics: titleForAnalytics,
    description: `${verb} ${absoluteDiff} ${noun}`,
    context: defaultLessonContext(payload, models),
    activity: defaultLessonActivity(payload, models),
    newAnalyticsEvents: [newEvent],
  }
}
